import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { usePrefersReducedMotion } from "./calm";

export type SceneKey = "company" | "data" | "review";

const TITLE_MS = 1900;

/**
 * A short title between two phases: hairlines run across the card and the line for the next phase
 * appears. It shows once, the first time the phase is reached, ends by itself, and is skipped with a click,
 * Enter or Esc. Same size as every step, so nothing around it moves.
 */
export default function TitleCard({ scene, onDone }: { scene: SceneKey; onDone: () => void }) {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    const timer = window.setTimeout(onDone, reduced ? 900 : TITLE_MS);
    return () => window.clearTimeout(timer);
  }, [onDone, reduced]);

  const line = (y: number, delay: number) => (
    <path d={`M0 ${y}H400`} pathLength={1} className="onb-line-play" style={{ ["--onb-delay" as string]: `${delay}ms`, animationDuration: "1300ms" }} />
  );
  return (
    <div
      className="onb-card relative mx-auto flex w-full max-w-[1120px] cursor-pointer items-center justify-center overflow-hidden rounded-3xl border border-border bg-card shadow-sm"
      data-testid="title-card"
      data-scene={scene}
      role="button"
      tabIndex={0}
      aria-label={t("onboardingPage.scenes.skip")}
      onClick={onDone}
    >
      <svg className="pointer-events-none absolute inset-0 h-full w-full text-border" viewBox="0 0 400 240" preserveAspectRatio="none" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
        {line(70, 0)}
        {line(120, 160)}
        {line(170, 320)}
      </svg>
      <div className="relative px-6 text-center">
        <h2 aria-live="polite" className="onb-rise text-3xl font-semibold tracking-tight sm:text-4xl" style={{ ["--onb-delay" as string]: "500ms" }}>
          {t(`onboardingPage.scenes.${scene}`)}
        </h2>
        <p className="onb-fade mt-3 text-xs text-muted-foreground" style={{ ["--onb-delay" as string]: "900ms" }}>
          {t("onboardingPage.scenes.skip")}
        </p>
      </div>
    </div>
  );
}
